import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Button } from "@/atoms/button";
import { Crescent } from "@/atoms/crescent";
import { EmptyState } from "@/components/empty-state";
import { useInvoiceDetails } from "@/data/invoice";
import { coachLabel } from "@/utils/coach";

/**
 * `/invoice/:id/pdf`: a link in an email that goes straight to the document.
 *
 * The wait is the 48 crescent with its label, never a spinner. Nothing in Gymkee spins, and this
 * page is the whole of what a person sees for the second it takes to resolve the URL.
 */

export function InvoicePdfPage() {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { data, isLoading, isError } = useInvoiceDetails(invoiceId);

  useEffect(() => {
    if (data?.invoice.invoicePdf)
      window.location.href = data.invoice.invoicePdf;
  }, [data]);

  const failed = !invoiceId || isError || (!!data && !data.invoice.invoicePdf);
  const coachName = coachLabel(data?.business.name, t);
  const supportEmail = data?.business.supportEmail;

  return (
    <div className="bg-surface-paper px-gk-4 flex min-h-screen items-center justify-center">
      {failed && !isLoading ? (
        <div className="w-full max-w-[520px]">
          <EmptyState
            heading={t("gymkeePay.invoice.openFailed")}
            line={t("gymkeePay.invoice.openFailedLine")}
            inset={false}
            action={
              supportEmail ? (
                <Button variant="tertiary" href={`mailto:${supportEmail}`}>
                  {t("gymkeePay.common.messageCoach", { coach: coachName })}
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <Crescent
          size={48}
          delay={0}
          label={t("gymkeePay.common.openingInvoice")}
        />
      )}
    </div>
  );
}
